import { Table } from "@tanstack/react-table";
import { useEffect, useRef } from "react";

import { TFunctions } from "./CustomTable";
import { DataTableActionsFloatingMenu } from "./DataTableActionsFloatingMenu";
import { ExportManager } from "./ExportManager";
import Search from "./SearchFunction";

import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDataTableSlotContext } from "@/context/DataTableSlot";
import { useQueryFilterType } from "@/hooks/useQueryParams";
import {
  ExtendedColumnDef,
  TAny,
  _TFloatingMenuCustomComponentProps,
} from "@/interfaces/globals";
import { cn, formatToCapitalizedWords } from "@/lib/utils";
import Icons from "@/utils/Icons";

export interface ExtendedTFunctions<TData, TValue> {
  functions: TFunctions<TData, TValue> | undefined;
  hasWritePermission: boolean;
  columns: ExtendedColumnDef<TData, TValue>[];
  table: Table<TData>;
  data: TData[];
  filterOperation: useQueryFilterType;
  hide?: boolean;
}

const TableFunctions = <T, V>({
  functions,
  columns,
  data,
  table,
  hasWritePermission,
  filterOperation,
  hide,
}: ExtendedTFunctions<T, V>): React.JSX.Element => {
  const {
    isExport,
    search,
    columnToggle,
    add,
    floatingMenu,
    floatingMenuClassName,
    floatingMenuCustom,
    multiFloating,
    hasExportInFloatingMenu,
    custom,
    showRenderableFunctions = true,
  } = functions || {};

  const { setFunctions } = useDataTableSlotContext();
  const resetSearchRef = useRef<{ resetSearch: () => void }>(null);

  const resetSearch = (): void => {
    if (resetSearchRef?.current) {
      resetSearchRef.current.resetSearch();
    }
  };
  useEffect(() => {
    if (setFunctions) {
      // if (existingFunctions) {
      //   setFunctions(existingFunctions);
      // }
      setFunctions({
        functions,
        columns,
        data,
        table,
        hasWritePermission,
        filterOperation,
        resetSearch,
      } as TAny);
    }
  }, [
    columns,
    data,
    filterOperation,
    functions,
    hasWritePermission,
    setFunctions,
    table,
    JSON.stringify(setFunctions),
  ]);

  return (
    <>
      {showRenderableFunctions && !hide && (
        <div className='flex items-center gap-2'>
          {search && (
            <Search
              search={search}
              updateParams={filterOperation.updateParams}
              ref={resetSearchRef}
            />
          )}
          {custom && custom.position === "center" && custom.node}
          {hasWritePermission && add && (
            <ButtonWithIcon
              icon='add'
              variant={add?.variant}
              size={"sm"}
              className={cn(
                "text-sm font-normal dark:text-white",
                add?.className,
              )}
              disabled={add?.disabled}
              onClick={add.handleClick}
            >
              {add?.label}
            </ButtonWithIcon>
          )}
          {custom && custom.position === "left" && custom.node}
          {isExport && (
            <ExportManager columns={columns} data={data} table={table} />
          )}

          {columnToggle && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant={"ghost"}>
                  <Icons name='bar-chart' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {table
                  .getAllColumns()
                  .filter(
                    (column) =>
                      column.getCanHide() &&
                      !columns.find((hide) => hide.id === column.id)
                        ?.enableHiding,
                  )
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className='capitalize'
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {formatToCapitalizedWords(column.id)}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {custom && custom.position === "right" && custom.node}
        </div>
      )}
      {floatingMenu && (
        <DataTableActionsFloatingMenu
          // children={floatingMenuCustom}
          isExport={hasExportInFloatingMenu}
          className={floatingMenuClassName}
          columns={columns}
          data={data}
          table={table}
          multiFloating={multiFloating}
        >
          {floatingMenuCustom}
        </DataTableActionsFloatingMenu>
      )}
    </>
  );
};

export default TableFunctions;
